import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import { explainKvTokenSeconds, formatInteger } from '../lib/format';

/**
 * What the user wants to know on landing: what have I spent, and how much room
 * do I have left.
 */
export function Overview({ account }: { account: Account }) {
  const usage = useQuery({
    queryKey: ['usage', account.id],
    queryFn: () => client.usage(account.id),
  });

  return (
    <>
      <h1>Overview</h1>
      <p className="subtitle">
        Plan <strong>{account.plan}</strong>
        {account.status !== 'active' && (
          <>
            {' — '}
            <span className="error">account {account.status}</span>
          </>
        )}
      </p>

      {usage.isLoading && <p className="muted">Loading usage…</p>}
      {usage.isError && <p className="error">Could not load usage.</p>}

      {usage.data && (
        <>
          <div className="cards">
            <div className="card">
              <div className="label">Requests</div>
              <div className="value">{formatInteger(usage.data.requests)}</div>
              <div className="hint">this billing period</div>
            </div>
            <div className="card">
              <div className="label">KV-token-seconds</div>
              <div className="value">{formatInteger(usage.data.kv_token_seconds)}</div>
              {/* The unit is right and unfamiliar; the portal is where that
                  gets explained, or every ticket will be about it. */}
              <div className="hint">{explainKvTokenSeconds(usage.data.kv_token_seconds)}</div>
            </div>
            <div className="card">
              <div className="label">Peak KV tokens</div>
              <div className="value">{formatInteger(usage.data.peak_kv_tokens)}</div>
              <div className="hint">high-water mark, not a total</div>
            </div>
            <div className="card">
              <div className="label">Tokens</div>
              <div className="value">
                {formatInteger(usage.data.input_tokens + usage.data.output_tokens)}
              </div>
              <div className="hint">
                {formatInteger(usage.data.input_tokens)} in ·{' '}
                {formatInteger(usage.data.output_tokens)} out ·{' '}
                {formatInteger(usage.data.cached_input_tokens)} cached
              </div>
            </div>
          </div>

          <h2>Why KV-token-seconds?</h2>
          <p className="muted">
            The scarce resource on this service is KV memory, not requests. A session with a
            512k-token context occupies roughly twenty times the hardware of an ordinary 25k
            one, for as long as it is open — so that is what the meter reads, and what the
            price list is built on. Charging per output token would misprice a long-context
            session by about that same factor.
          </p>
        </>
      )}
    </>
  );
}
